import { Health } from '@capgo/capacitor-health'
import { Preferences } from '@capacitor/preferences'
import { RefuelledNutrition, type NutritionMeal } from '@refuelled/capacitor-nutrition'
import { api } from '@/api/client'
import type { DailyTotals } from '@/stores/members'

/**
 * Meals eaten, written out to Health Connect (S-70, ADR-037), so Garmin and
 * the rest see what was eaten beside what was done.
 *
 * Each record carries the meal log's id, so a meal corrected in Refuelled
 * replaces its record and one deleted is removed, wherever the change was
 * made. The figures are the ones we computed (rule 2), never new ones.
 */
const KEY_ON = 'health.writeMeals'
/** How far back a sync puts things right. */
export const MEALS_OUT_DAYS = 7
const ID_PREFIX = 'refuelled:'

/**
 * Health Connect wants a time, and a meal log has a slot. These are the
 * usual times for each; a slot's second entry follows a minute after its
 * first so that they keep their order.
 */
const SLOT_TIME: Record<DailyTotals['meals'][number]['slot'], [number, number]> = {
  breakfast: [8, 0],
  lunch: [12, 30],
  snack: [15, 30],
  dinner: [19, 0],
  // Drinks happen all day; one time keeps them together (ADR-039).
  drink: [10, 30],
  custom: [21, 0],
}
const MEAL_MINUTES = 15

/** Salt is sodium chloride; sodium is 40 % of it by mass. */
const SODIUM_PER_SALT = 0.4

export async function mealsOutOn(): Promise<boolean> {
  return (await Preferences.get({ key: KEY_ON })).value === '1'
}

/** Ask Health Connect for permission to write nutrition; true when given. */
export async function turnMealsOutOn(): Promise<boolean> {
  const status = await Health.requestAuthorization({ read: [], write: ['dietaryEnergyConsumed'] })
  const granted = (status.writeAuthorized ?? []).includes('dietaryEnergyConsumed')
  if (granted) await Preferences.set({ key: KEY_ON, value: '1' })
  return granted
}

/** Stop writing, and take back what was written: nothing stale is left behind. */
export async function turnMealsOutOff(): Promise<void> {
  await Preferences.remove({ key: KEY_ON })
  const { start, end } = windowFor(datesBack(MEALS_OUT_DAYS * 8))
  try {
    const { ids } = await RefuelledNutrition.listMeals({ start, end })
    if (ids.length) await RefuelledNutrition.deleteMeals({ ids })
  } catch {
    // Permission already withdrawn: Health Connect keeps what it has, and says so in its own settings.
  }
}

/** One day's entries as Health Connect records, at local times on that date. */
export function mealsForHealth(date: string, meals: DailyTotals['meals']): NutritionMeal[] {
  const seen = new Map<string, number>()
  return meals.map((meal) => {
    const n = seen.get(meal.slot) ?? 0
    seen.set(meal.slot, n + 1)
    const [hour, minute] = SLOT_TIME[meal.slot]
    const [y, m, d] = date.split('-').map(Number) as [number, number, number]
    const start = new Date(y, m - 1, d, hour, minute + n)
    const end = new Date(start.getTime() + MEAL_MINUTES * 60_000)
    return {
      id: `${ID_PREFIX}${meal.id}`,
      start: start.toISOString(),
      end: end.toISOString(),
      // Health Connect has no meal type for a drink.
      mealType: meal.slot === 'custom' || meal.slot === 'drink' ? 'unknown' : meal.slot,
      name: meal.title,
      energyKcal: meal.energyKcal,
      proteinG: meal.proteinG,
      carbG: meal.carbG,
      fatG: meal.fatG,
      fibreG: meal.fibreG,
      sugarG: meal.sugarG,
      sodiumG: meal.saltG === null ? null : meal.saltG * SODIUM_PER_SALT,
    }
  })
}

/** Today and the days before it, oldest first, as local dates. */
export function datesBack(days: number, from = new Date()): string[] {
  return Array.from({ length: days }, (_, i) => {
    const d = new Date(from.getFullYear(), from.getMonth(), from.getDate() - (days - 1 - i))
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  })
}

/** Local midnight before the first date to local midnight after the last. */
function windowFor(dates: string[]): { start: string; end: string } {
  const at = (date: string, plusDays = 0) => {
    const [y, m, d] = date.split('-').map(Number) as [number, number, number]
    return new Date(y, m - 1, d + plusDays).toISOString()
  }
  return { start: at(dates[0]!), end: at(dates[dates.length - 1]!, 1) }
}

/**
 * Write the person's meals for the last week, and remove any record of a
 * meal that is no longer logged. Only this person's: Health Connect is the
 * phone owner's, not the household's.
 */
export async function writeMealsOut(memberId: string, days = MEALS_OUT_DAYS): Promise<{ written: number; removed: number }> {
  const dates = datesBack(days)
  const meals: NutritionMeal[] = []
  for (const date of dates) {
    const totals = await api.get<DailyTotals[]>(`/logs/day/${date}`, { memberId })
    const mine = totals.find((t) => t.memberId === memberId)
    if (mine) meals.push(...mealsForHealth(date, mine.meals))
  }
  if (meals.length) await RefuelledNutrition.writeMeals({ meals })

  const keep = new Set(meals.map((m) => m.id))
  const { ids } = await RefuelledNutrition.listMeals(windowFor(dates))
  const gone = ids.filter((id) => id.startsWith(ID_PREFIX) && !keep.has(id))
  if (gone.length) await RefuelledNutrition.deleteMeals({ ids: gone })
  return { written: meals.length, removed: gone.length }
}
